import { css } from '@emotion/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Badge, Box, Fab } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { Ref, Suspense, useLayoutEffect } from 'react';
import { Panel, PanelImperativeHandle, Separator, usePanelRef } from 'react-resizable-panels';
import { Breakpoint } from '../../constants/styles';
import { isNavigationOpenAtom } from '../Layout/state/NavigationState';
import { LoadingBarWhileMounted } from '../LoadingBar/LoadingBarWhileMounted';
import { LoadingId } from '../../types';
import { assistantAttentionAtom, ATTENTION_MESSAGE } from './assistantAttentionStateJ';
import { useChatBotPanelStateSync } from './chatbot-panel-hooks';
import { ShowChatBotAtom } from './chatbot-state';
import { useAssistant } from './useAssistant';
import { lazyShell, Preload } from '../../util/lazyPage';

export { ShowChatBotAtom };

// The ChatBot (and the editor it embeds) stays out of the boot bundle; the warm-up fetches it early
// and the panel's first open loads it otherwise.
const ChatBot = lazyShell(() => import('../../pages/ChatBot/ChatBot'), 'default');
export const preloadChatBot: Preload = () => ChatBot.preload();

export const ChatBotDrawerDefaultWidth = 500;
const ChatBotDrawerStyles = css`
  border-left: var(--mui-palette-primary-light) 1px solid;
  width: 100%;
  position: relative;
  min-height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  background: var(--mui-palette-background-default);
`;

export function ChatBotButton() {
  const showChatBot = useAtomValue(ShowChatBotAtom);
  // An Atlas conversation that needs the user while the panel is closed.
  const attention = useAtomValue(assistantAttentionAtom);
  const { openAssistant } = useAssistant();

  if (showChatBot) {
    // The panel's own header close button takes over while open; an empty cell
    // keeps the app-header grid columns stable (same as the audit-view case).
    return <div />;
  }

  const label = attention
    ? `open foresight assistant: ${ATTENTION_MESSAGE[attention.reason]}`
    : 'open foresight assistant';
  return (
    <Badge color='error' variant='dot' overlap='circular' invisible={!attention} sx={{ justifySelf: 'end' }}>
      <Fab
        aria-label={label}
        color='primary'
        size='small'
        title='foresight assistant'
        // The conversation that needs the user opens on the history the closed panel missed.
        onClick={() => openAssistant(attention ? { conversation: attention.conversationId } : {})}
        sx={{ zIndex: 0 }}
      >
        <AutoAwesomeIcon />
      </Fab>
    </Badge>
  );
}

export function ChatBotDrawer() {
  const showChatBot = useAtomValue(ShowChatBotAtom);
  if (!showChatBot) {
    return null;
  }
  return (
    <Box css={ChatBotDrawerStyles}>
      <Suspense fallback={<LoadingBarWhileMounted id={LoadingId.loadChatBot} />}>
        <ChatBot />
      </Suspense>
    </Box>
  );
}

function useCloseNavbarOnChatBotOpen() {
  const setShowSideNav = useSetAtom(isNavigationOpenAtom);
  const showChatBot = useAtomValue(ShowChatBotAtom);
  useLayoutEffect(() => {
    if (showChatBot && window.innerWidth < Breakpoint.xLarge) {
      setShowSideNav(false);
    }
  }, [showChatBot, setShowSideNav]);
}

export function ChatBotSection() {
  const panelRef = usePanelRef();
  useCloseNavbarOnChatBotOpen();
  const onResize = useChatBotPanelStateSync(panelRef);

  return (
    <>
      <Separator />
      <Panel
        // Type assertion needed due to library typing mismatch between usePanelRef return type and panelRef prop type
        panelRef={panelRef as Ref<PanelImperativeHandle>}
        id='chatbot-drawer'
        collapsible
        collapsedSize={0}
        defaultSize={0}
        onResize={onResize}
        minSize={350}
      >
        <ChatBotDrawer />
      </Panel>
    </>
  );
}
